/**
 * usePackQueue — "its first looks" queue, read/write from the pack page (narcis_funnel plan,
 * Step 1). Two backing stores depending on `visitorState`:
 *
 *   - anon:            localStorage only (`utils/anonPackQueue.ts`), context `pre_signup`.
 *   - signed/training/owner: the real `PackQueueItem` table via `/api/pack-queue`
 *     (`routes/api.pack-queue.tsx`, wired in Step 0). Context `waiting` for a training visitor,
 *     `owner` for an owner — both map to the same DB-backed path here; the caller passes the
 *     right context per queue action.
 *
 * On the first authenticated mount, if the browser still holds an anon queue, it's folded into
 * the DB queue once (`merge-anon`, Architecture decision 3) before anything else reads the DB
 * queue — so a user who queued packs anonymously sees them in "its first looks" the moment they
 * sign in, per the B-state note ("your queue is kept").
 */
import {useState, useEffect, useCallback, useRef} from 'react'
import type {PackQueueContext} from '@prisma/client'
import type {VisitorState} from '@core/products/visitorState.server'
import {emitFunnelQueueEvent} from '@core/products/funnelEvents'
import {
    getAnonQueue,
    addToAnonQueue,
    removeFromAnonQueue,
    hasMergedAnonQueue,
    markAnonQueueMerged,
    ANON_QUEUE_CAP,
} from '@product/narcis/utils/anonPackQueue'
import {isQueueFull} from '@product/narcis/packs/queueFullState'

// Single source of truth for the three queue contexts is the Prisma schema's `PackQueueContext`
// enum (schema.prisma) — a hand-declared union here would drift from it silently.
export type {PackQueueContext}

export interface PackQueueEntry {
    packSlug: string
    position: number
}

export function usePackQueue(visitorState: VisitorState) {
    const isAuthed = visitorState !== 'anon'
    // Starts empty on every render path, server and client alike — localStorage doesn't exist on
    // the server, so seeding this from getAnonQueue() at init time would read [] on the SSR pass
    // and the real list on the client's hydration pass, a hydration mismatch for any anon visitor
    // who already has this pack queued from a prior visit. The effect below hydrates it post-mount
    // instead.
    const [queue, setQueue] = useState<PackQueueEntry[]>([])
    const mergeStartedRef = useRef(false)

    useEffect(() => {
        if (isAuthed) return
        setQueue(getAnonQueue().map((packSlug, i) => ({packSlug, position: i + 1})))
    }, [isAuthed])

    const refetchServerQueue = useCallback(async () => {
        try {
            const res = await fetch('/api/pack-queue', {credentials: 'include'})
            if (!res.ok) return
            const data = await res.json()
            const rows = (data.queue ?? []) as Array<{packSlug: string; position: number}>
            setQueue(rows.map((r) => ({packSlug: r.packSlug, position: r.position})))
        } catch {
            // Non-fatal — the queue chip/CTA falls back to "not queued" until the next refetch.
        }
    }, [])

    // Merge-then-fetch, once, on first authenticated mount.
    useEffect(() => {
        if (!isAuthed || mergeStartedRef.current) return
        mergeStartedRef.current = true
        ;(async () => {
            const anonSlugs = getAnonQueue()
            if (anonSlugs.length > 0 && !hasMergedAnonQueue()) {
                try {
                    await fetch('/api/pack-queue', {
                        method: 'POST',
                        credentials: 'include',
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify({intent: 'merge-anon', packSlugs: anonSlugs, context: 'pre_signup'}),
                    })
                } catch {
                    // Best-effort — markAnonQueueMerged is skipped below on failure, so a later
                    // mount (this pack page or another) retries the merge.
                    await refetchServerQueue()
                    return
                }
                markAnonQueueMerged()
            }
            await refetchServerQueue()
        })()
    }, [isAuthed, refetchServerQueue])

    const queuePack = useCallback(async (packSlug: string, context: PackQueueContext) => {
        if (!isAuthed) {
            const result = addToAnonQueue(packSlug)
            setQueue(getAnonQueue().map((s, i) => ({packSlug: s, position: i + 1})))
            if (result.status === 'queued') emitFunnelQueueEvent({name: 'pack_queued', packSlug, context: 'pre_signup'})
            return result
        }
        try {
            const res = await fetch('/api/pack-queue', {
                method: 'POST',
                credentials: 'include',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({intent: 'queue', packSlug, context}),
            })
            const result = await res.json()
            // Fires here, not server-side (GA4 collection policy, @core/utils/CLAUDE.md) — this
            // fetcher's resolution IS the browser moment the click produced; the server action
            // that ran in between emits nothing.
            if (result.status === 'queued') emitFunnelQueueEvent({name: 'pack_queued', packSlug, context})
            await refetchServerQueue()
            return result as {status: 'queued' | 'already-queued'; position: number} | {status: 'cap-reached'}
        } catch {
            return {status: 'cap-reached'} as const
        }
    }, [isAuthed, refetchServerQueue])

    // No `context` param: the DB row already carries the context it was queued under —
    // unqueuePack (packQueue.server.ts) returns it on the row it deletes, since the client has no
    // other way to know it; the anon path always unqueues from `pre_signup`, the only context an
    // anon queue can hold.
    const unqueuePack = useCallback(async (packSlug: string) => {
        if (!isAuthed) {
            const result = removeFromAnonQueue(packSlug)
            setQueue(getAnonQueue().map((s, i) => ({packSlug: s, position: i + 1})))
            if (result.status === 'unqueued') emitFunnelQueueEvent({name: 'pack_unqueued', packSlug, context: 'pre_signup'})
            return result
        }
        try {
            const res = await fetch('/api/pack-queue', {
                method: 'POST',
                credentials: 'include',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({intent: 'unqueue', packSlug}),
            })
            const result = await res.json()
            if (result.status === 'unqueued') emitFunnelQueueEvent({name: 'pack_unqueued', packSlug, context: result.context})
            await refetchServerQueue()
            return result as {status: 'unqueued' | 'not-queued'}
        } catch {
            return {status: 'not-queued'} as const
        }
    }, [isAuthed, refetchServerQueue])

    const entryFor = useCallback((packSlug: string) => queue.find((q) => q.packSlug === packSlug) ?? null, [queue])

    return {queue, queuePack, unqueuePack, entryFor, isFull: isQueueFull(queue.length, ANON_QUEUE_CAP)}
}
